import {
  Bot,
  ChartNoAxesCombined,
  Megaphone,
  Rocket,
  Search,
  Sparkles,
  Users,
} from "lucide-react";

export const logoUrl =
  "https://customer-assets.emergentagent.com/job_89d1442d-c529-4b83-975c-e20d73903df3/artifacts/vdxhb4dg_1000020338.jpg";

export const heroBackgroundUrl = "https://images.unsplash.com/photo-1710438399422-2fca27686bcd?auto=format&fit=crop&w=1600&q=80";
export const caseStudyBackdropUrl = "https://images.pexels.com/photos/921290/pexels-photo-921290.png?auto=compress&cs=tinysrgb&w=1400";
export const teamOfficeUrl = "https://images.pexels.com/photos/6804068/pexels-photo-6804068.jpeg?auto=compress&cs=tinysrgb&w=1400";

export const heroWords = [
  {
    word: "Discovered",
    textClass: "text-white",
    background: "radial-gradient(circle at 75% 20%, rgba(37, 99, 235, 0.24), transparent 24%), radial-gradient(circle at 15% 25%, rgba(255, 255, 255, 0.08), transparent 18%), linear-gradient(135deg, #030408 0%, #07111d 100%)",
  },
  {
    word: "Trusted",
    textClass: "text-blue-200",
    background: "radial-gradient(circle at 75% 20%, rgba(37, 99, 235, 0.28), transparent 22%), radial-gradient(circle at 15% 25%, rgba(255, 255, 255, 0.08), transparent 18%), linear-gradient(135deg, #030408 0%, #09121f 100%)",
  },
  {
    word: "Installed",
    textClass: "text-slate-100",
    background: "radial-gradient(circle at 75% 20%, rgba(255, 255, 255, 0.12), transparent 22%), radial-gradient(circle at 20% 30%, rgba(37, 99, 235, 0.16), transparent 16%), linear-gradient(135deg, #030408 0%, #0b1019 100%)",
  },
];

export const systems = [
  {
    slug: "aso-growth-engine",
    title: "ASO Growth Engine",
    description: "Improves discoverability, store conversion, and ratings with a structured app-store growth layer.",
    icon: Rocket,
    glow: "from-blue-500/20 to-white/10",
    testId: "systems-card-1",
    outcome: "+40% installs",
    what: "Builds stronger keyword visibility, better conversion, and healthier app-store trust.",
    different: "Equinox turns ASO into a live system with reviews, conversion, and feedback loops.",
    outcomes: ["Higher install conversion", "Better review quality", "Lower blended CAC"],
  },
  {
    slug: "paid-acquisition-engine",
    title: "Paid Acquisition Engine",
    description: "Scales performance media with sharper attribution, faster creative learning, and tighter CAC control.",
    icon: Megaphone,
    glow: "from-white/10 to-blue-500/20",
    testId: "systems-card-2",
    outcome: "30–60% CAC reduction",
    what: "Optimises paid acquisition across Meta, Google, and high-intent funnel touchpoints.",
    different: "Equinox blends media buying with landing-page and product conversion signals.",
    outcomes: ["Lower CAC", "Faster creative iteration", "More predictable scale"],
  },
  {
    slug: "review-rating-system",
    title: "Review & Rating System",
    description: "Creates a consistent review flow that protects ratings, trust, and acquisition performance over time — and we can seed reviews on any platform at the most economical cost.",
    icon: Sparkles,
    glow: "from-amber-300/15 to-white/10",
    testId: "systems-card-3",
    outcome: "4.6⭐ average maintained",
    what: "Stabilises sentiment, improves freshness, raises trust in the app-store environment, and supports economical review seeding where needed.",
    different: "Equinox treats ratings and reviews like a performance lever, not a support metric.",
    outcomes: ["Higher trust", "Stronger ratings", "Better install conversion"],
  },
  {
    slug: "funnel-optimization-system",
    title: "Funnel Optimization System",
    description: "Improves the path from click to install or lead with better flows, messaging, and conversion design.",
    icon: Bot,
    glow: "from-blue-500/16 to-amber-300/12",
    testId: "systems-card-4",
    outcome: "+22% conversion lift",
    what: "Fixes drop-offs across landing pages, onboarding flows, and lead journeys.",
    different: "Equinox optimises the full growth path instead of only the top-of-funnel channel.",
    outcomes: ["Higher conversion", "Lower leak in the funnel", "More efficient paid scale"],
  },
  {
    slug: "influencer-growth-engine",
    title: "Influencer Growth Engine",
    description: "Builds creator-led reach and trust with performance logic instead of vanity placements.",
    icon: Users,
    glow: "from-white/10 to-blue-500/16",
    testId: "systems-card-5",
    outcome: "+28% creator-led uplift",
    what: "Activates creators and influencer-led campaigns for trust-rich acquisition.",
    different: "Equinox tracks influencer work against business outcomes, not vanity engagement alone.",
    outcomes: ["Higher trust-led reach", "Better creator ROI", "More efficient content-led growth"],
  },
  {
    slug: "ai-automation-systems",
    title: "AI Automation Systems",
    description: "Automates routing, reporting, and performance actions so growth execution stays fast and scalable.",
    icon: Search,
    glow: "from-emerald-400/12 to-blue-500/16",
    testId: "systems-card-6",
    outcome: "3x faster execution",
    what: "Reduces manual growth work across funnels, reporting, and lead handling.",
    different: "Equinox builds automation around growth operations instead of generic no-code workflows.",
    outcomes: ["Faster team execution", "Better response speed", "Scalable operating flow"],
  },
];

export const processSteps = [
  {
    title: "Audit & Strategy",
    description: "We diagnose growth blockers, signal gaps, CAC inefficiencies, and missed conversion opportunities.",
  },
  {
    title: "AI System Deployment",
    description: "We deploy the actual system layer — automation, attribution, creative loops, and channel execution.",
  },
  {
    title: "Scale & Optimization",
    description: "We keep improving performance through testing, reporting, and ongoing growth compounding.",
  },
];

export const proofBarItems = [
  "App Reputation + ASO",
  "Organic App Growth",
  "Store Conversion Systems",
  "Review Intelligence",
];

export const homepageProofStudies = [
  {
    slug: "pop-upi",
    client: "POP UPI",
    positioning: "App Reputation + ASO + organic app growth",
    summary: "Managed reputation, store presence, and organic app-growth momentum for a high-usage payments product.",
    metrics: [
      { value: "4.7★", label: "Rating" },
      { value: "+20K", label: "Reviews MoM" },
      { value: "+19%", label: "Installs MoM" },
    ],
  },
  {
    slug: "zet",
    client: "ZET",
    positioning: "App Reputation + ASO + organic growth",
    summary: "Managed trust signals, review growth, and organic store performance for a scaling app category leader.",
    metrics: [
      { value: "4.8★", label: "Rating" },
      { value: "+2.5K", label: "Reviews MoM" },
      { value: "+7%", label: "Installs MoM" },
    ],
  },
  {
    slug: "littlebox",
    client: "Littlebox",
    positioning: "App-store presence + reputation + organic growth support",
    summary: "Strengthened app-store presence and reputation to support more credible organic growth over time.",
    metrics: [
      { value: "4.6★", label: "Rating" },
      { value: "+2K", label: "Organic reviews" },
      { value: "Organic", label: "Install support" },
    ],
  },
];

export const dashboardStats = [
  { label: "Apps monitored", value: "70+", tone: "from-white to-blue-200" },
  { label: "Signals syncing", value: "124", tone: "from-slate-100 to-white/70" },
  { label: "CAC efficiency", value: "-40%", tone: "from-amber-200 to-white" },
];

export const aiSuggestions = [
  "Generate growth strategy...",
  "Optimise creator pipeline...",
  "Diagnose paid ad performance...",
  "Automate follow-up journeys...",
];

export const aiActions = ["Analyze", "Generate", "Optimize"];

export const pageNavLinks = [
  { label: "Home", href: "/" },
  { label: "ASO AI Engine", href: "/aso-ai-engine" },
  { label: "Services", href: "/services" },
  { label: "How We Scale Apps", href: "/how-we-scale-apps" },
  { label: "Case Studies", href: "/case-studies" },
  { label: "Gives Back", href: "/gives-back" },
  { label: "About", href: "/about" },
  { label: "Insights", href: "/insights" },
  { label: "Contact", href: "/contact" },
];

export const founderImageUrl =
  "https://customer-assets.emergentagent.com/job_automation-engine-16/artifacts/5mn0ytkf_1000097900.jpeg";

export const realCaseStudies = [
  {
    slug: "pop-upi",
    client: "POP UPI",
    type: "Fintech",
    headline: "App reputation and ASO systems for measurable organic app growth.",
    summary: "Equinox managed POP UPI across rating quality, review momentum, store reputation, and organic growth support.",
    problem: "The app needed stronger public trust, healthier review momentum, and a sharper store-growth foundation to support month-over-month install improvement.",
    solution: "Equinox handled App Reputation + ASO + organic app growth support, combining review intelligence, store conversion improvements, and reputation management.",
    numbers: ["4.7★ rating", "+20K reviews MoM", "+19% installs MoM"],
    results: ["Managed and maintained a 4.7★ app rating", "Achieved approximately 20K review growth month-over-month", "Delivered approximately 19% month-over-month increase in installs"],
    outcomes: [
      "Strengthened app reputation at scale",
      "Improved review velocity month-over-month",
      "Supported stronger organic app growth through ASO and store trust",
    ],
  },
  {
    slug: "zet",
    client: "ZET",
    type: "Fintech",
    headline: "Managed rating quality and review growth for sustainable organic installs.",
    summary: "Equinox supported ZET with a trust-first app-growth approach focused on reputation, ASO, and organic install momentum.",
    problem: "The product needed consistent store trust signals and healthier month-over-month organic growth support in a highly competitive app category.",
    solution: "Equinox managed App Reputation + ASO + organic growth execution, combining rating maintenance, review intelligence, and store conversion support.",
    numbers: ["4.8★ rating", "+2.5K reviews MoM", "+7% installs MoM"],
    results: ["Managed and maintained a 4.8★ app rating", "Achieved approximately 2.5K review growth month-over-month", "Delivered approximately 7% month-over-month increase in installs"],
    outcomes: [
      "Maintained a strong trust profile in-store",
      "Improved review growth month-over-month",
      "Supported organic app growth through reputation and ASO execution",
    ],
  },
  {
    slug: "littlebox",
    client: "Littlebox",
    type: "Commerce",
    headline: "Store presence and reputation support for stronger organic install momentum.",
    summary: "Equinox helped Littlebox improve store perception and app reputation to support healthier organic growth.",
    problem: "The app needed a stronger public reputation layer and better app-store presence to support install growth more organically.",
    solution: "Equinox worked across App Reputation + ASO + review intelligence to improve store credibility, rating quality, and conversion confidence.",
    numbers: ["4.6★ rating", "+2K additional reviews organically", "Improved app-store presence"],
    results: ["Managed and maintained a 4.6★ app rating", "Achieved approximately 2K additional reviews organically", "Supported increased installs through improved app-store presence and reputation"],
    outcomes: [
      "Improved store trust and visibility",
      "Built stronger organic review momentum",
      "Supported install growth through better reputation and presence",
    ],
  },
  {
    slug: "plusgold",
    client: "PlusGold",
    type: "Client Portfolio",
    headline: "Verified client work included in the Equinox app-growth portfolio.",
    summary: "PlusGold is part of Equinox’s client work portfolio. Only verified public results are shown on the website, and no unverified metrics are being displayed here.",
    problem: "Client-specific challenge details are not being publicly expanded on this page without verified approved public metrics.",
    solution: "Equinox positioning for this engagement sits within app growth, reputation, ASO, and conversion support where relevant to the client’s store presence.",
    numbers: ["Verified client work", "No fabricated metrics", "Private scope"],
    results: ["Included in Equinox client portfolio", "Detailed verified performance figures are not displayed publicly here", "No unverified numbers have been added"],
    outcomes: [
      "Client included with credibility-safe positioning",
      "No fabricated claims used",
      "Public proof kept within verified-data rules",
    ],
  },
  {
    slug: "amul",
    client: "Amul",
    type: "Client Portfolio",
    headline: "Verified client work presented without unapproved public performance figures.",
    summary: "Amul is included in Equinox’s client portfolio using only credibility-safe language. No new metrics or performance claims have been fabricated for this website.",
    problem: "Client-specific challenge details are intentionally kept high-level here because no approved public performance figures exist inside the project content.",
    solution: "Equinox service relevance for this work aligns with growth systems, reputation support, store visibility, and conversion-oriented execution where applicable.",
    numbers: ["Verified client work", "No fabricated metrics", "Public-safe proof"],
    results: ["Included in Equinox client portfolio", "Detailed verified performance figures are not displayed publicly here", "No unverified metrics have been introduced"],
    outcomes: [
      "Client included with high-integrity proof handling",
      "No invented figures added",
      "Positioning kept within verified-data constraints",
    ],
  },
  {
    slug: "zivame",
    client: "Zivame",
    type: "E-commerce",
    headline: "Purchase-intent lift through review momentum and user-experience signals.",
    summary: "Existing verified project content for Zivame focuses on trust, review freshness, and stronger purchase intent at consideration moments.",
    problem: "The brand needed more trust and stronger decision-making at the moment of purchase consideration.",
    solution: "Equinox improved review freshness, app-store quality signals, and trust-led growth messaging to support conversion intent.",
    numbers: ["4,000+ reviews/month", "+15% purchase intent"],
    results: ["Purchase intent increased", "Blended CAC improved through better conversion intent", "Review freshness strengthened trust"],
    outcomes: [
      "Created a stronger trust layer for product discovery",
      "Improved review freshness and quality",
      "Supported a better conversion environment",
    ],
  },
  {
    slug: "clovia",
    client: "Clovia",
    type: "E-commerce",
    headline: "Engagement and rating improvement for a consumer commerce app.",
    summary: "Existing verified project content for Clovia shows engagement and rating improvement tied to reviews, retention cues, and store-sentiment optimisation.",
    problem: "The brand wanted stronger engagement and healthier public perception without relying only on discount-driven growth.",
    solution: "We implemented an app engagement loop connected to reviews, retention cues, and store-sentiment optimisation.",
    numbers: ["3,500+ reviews/month", "+17% engagement", "+0.5★ rating improvement"],
    results: ["Engagement improved", "Return-visit efficiency increased", "Ratings moved up by 0.5★"],
    outcomes: [
      "Lifted engagement through sharper growth loops",
      "Improved public ratings and app confidence",
      "Supported stronger repeat-user behavior",
    ],
  },
];

export const footerDetails = {
  email: "shivam@equinoxmarketingagency.in",
  phone: "9798783598",
  whatsapp: "+91 9798783598",
  whatsappHref: "https://wa.me/919798783598",
  instagram: "@equinoxmarketingagency",
  instagramHref: "https://www.instagram.com/equinoxmarketingagency?igsh=bm9ibWNyajVpbWVj",
  linkedin: "Shivam Ray",
  linkedinHref: "https://www.linkedin.com/in/rayshivam09?utm_source=share_via&utm_content=profile&utm_medium=member_ios",
  address: "Radiant Brij Shail Plaza, Exhibition Road, Patna (800001), Bihar",
  gstin: "10GRCPR1650E",
};

export const homeResultsPreview = [
  {
    title: "POP UPI",
    metric: "+19% installs MoM",
    detail: "Managed a 4.7★ rating while driving approximately 20K reviews month-over-month.",
  },
  {
    title: "ZET",
    metric: "+7% installs MoM",
    detail: "Managed and maintained a 4.8★ rating with approximately 2.5K reviews month-over-month.",
  },
  {
    title: "Littlebox",
    metric: "+2K reviews organically",
    detail: "Maintained a 4.6★ rating and supported stronger installs through improved store presence and reputation.",
  },
];

export const insightsArticles = [
  {
    slug: "how-ai-growth-systems-lower-cac",
    title: "How AI growth systems lower CAC without slowing scale",
    category: "Acquisition",
    excerpt: "A practical look at why the best growth teams connect creative, attribution, and funnel logic instead of running disconnected campaigns.",
  },
  {
    slug: "why-review-velocity-is-a-growth-lever",
    title: "Why review velocity is one of the most underrated growth levers",
    category: "ASO",
    excerpt: "Ratings and reviews do more than build trust — they shape conversion, retention expectations, and paid efficiency.",
  },
  {
    slug: "what-high-performing-growth-agencies-actually-build",
    title: "What high-performing growth agencies actually build",
    category: "Systems",
    excerpt: "The difference between campaign execution and a real growth system layer for high-ticket brands.",
  },
  {
    slug: "funnel-optimization-for-app-scale",
    title: "Funnel optimization for app scale: where most teams leak performance",
    category: "Conversion",
    excerpt: "A sharper breakdown of where conversion drops, how to diagnose it, and what to fix first.",
  },
];

export const growthOsFeatures = [
  {
    title: "Automation engine",
    text: "Handles repetitive growth workflows, lead routing, and performance updates automatically.",
  },
  {
    title: "Attribution system",
    text: "Connects channels, conversions, and campaign signals into one decision layer.",
  },
  {
    title: "Creative testing",
    text: "Continuously identifies the creatives and messages that lower CAC faster.",
  },
  {
    title: "WhatsApp funnel",
    text: "Moves leads from click to qualification with automated, high-intent messaging flows.",
  },
];

export const growthOsActivity = [
  "Creative winner identified for fintech campaign",
  "WhatsApp lead scored and routed to founder team",
  "Retention signal improved across high-LTV cohort",
  "Store-conversion uplift detected after review momentum spike",
];

export const legalPages = {
  "terms-and-conditions": {
    title: "Terms & Conditions",
    intro: "These terms govern how visitors and clients interact with Equinox Marketing Agency and its digital properties.",
    sections: [
      { heading: "Use of Services", body: "All services, strategy sessions, growth systems, and deliverables are subject to mutual commercial agreement and final written confirmation." },
      { heading: "Intellectual Property", body: "All branding, frameworks, strategy material, and creative outputs remain protected unless explicitly transferred through a signed agreement." },
      { heading: "Commercial Scope", body: "Project timelines, revisions, reporting cycles, and execution scope depend on the package, proposal, and client approvals in force." },
    ],
  },
  "privacy-policy": {
    title: "Privacy Policy",
    intro: "Equinox respects visitor and client privacy and uses submitted information only for communication, lead qualification, and service delivery purposes.",
    sections: [
      { heading: "Data Collection", body: "We may collect contact details, WhatsApp number, business links, and enquiry details submitted through the website or direct outreach channels." },
      { heading: "Data Usage", body: "Submitted information is used for responding to enquiries, building custom plans, service coordination, and improving growth workflows." },
      { heading: "Data Protection", body: "We take reasonable steps to protect contact and enquiry data and do not intentionally share it outside essential service operations." },
    ],
  },
  terms: {
    title: "Terms of Use",
    intro: "By using this website, you agree to engage with Equinox content and enquiry systems responsibly and lawfully.",
    sections: [
      { heading: "Website Access", body: "Users may browse the site, case studies, and contact flows for informational and commercial evaluation purposes only." },
      { heading: "Accuracy", body: "While Equinox aims to keep information accurate and premium in presentation, all strategic or commercial commitments are final only after direct confirmation." },
      { heading: "Restrictions", body: "Users must not misuse the website, disrupt lead forms, or copy branded content in a misleading manner." },
    ],
  },
  "returns-and-refunds": {
    title: "Returns & Refunds",
    intro: "Because Equinox delivers strategic, marketing, and automation services, refund and cancellation terms depend on the approved scope and commercial agreement.",
    sections: [
      { heading: "Service-Based Nature", body: "Most Equinox offerings are service-led and time-based, so refunds are generally handled according to project stage, work completed, and signed commercial terms." },
      { heading: "Cancellation", body: "Cancellation requests should be raised directly through official Equinox communication channels for evaluation." },
      { heading: "Resolution", body: "Where applicable, Equinox will review refund or adjustment requests fairly and respond based on project status and agreed commitments." },
    ],
  },
};

export const credibilityBadges = [
  { icon: Sparkles, label: "AI-native workflows" },
  { icon: ChartNoAxesCombined, label: "Operator-led growth systems" },
  { icon: Bot, label: "WhatsApp & funnel automation" },
];